import { useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BRAND, BrandMark } from '../brand.js';
import { IconStar } from './Icons.js';
import { SearchCommand } from './SearchCommand.js';
import { IconTelegram, IconX, Landing } from '../pages/Landing.js';
import { TokenTicker } from './TokenTicker.js';
import { useMapStore } from '../store/mapStore.js';
import { api, clearAuth, getEmail, getToken, onUnauthorized } from '../lib/api.js';
import { useChainStore } from '../lib/chains.js';

type AuthState = 'checking' | 'open' | 'signed-in' | 'signed-out' | 'unreachable';

/**
 * App chrome + auth gate.
 *
 * Asks the API whether auth is enabled. If it is, the stored token is
 * validated against /auth/me; any later 401 (expired session, logout in
 * another tab) bounces to /signin and returns here after login.
 */
export function AppShell() {
  const toast = useMapStore((s) => s.toast);
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuthState] = useState<AuthState>('checking');
  const [email, setEmail] = useState(getEmail());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let authEnabled: boolean;
      try {
        ({ authEnabled } = await api.authConfig());
      } catch {
        // Can't tell whether auth is on — fail closed, never fall back to "open".
        if (!cancelled) setAuthState('unreachable');
        return;
      }
      if (!authEnabled) {
        if (!cancelled) {
          void useChainStore.getState().load();
          setAuthState('open');
        }
        return;
      }
      if (!getToken()) {
        if (!cancelled) setAuthState('signed-out');
        return;
      }
      try {
        const me = await api.me();
        if (!cancelled) {
          setEmail(me.email);
          void useChainStore.getState().load();
          setAuthState('signed-in');
        }
      } catch {
        if (!cancelled) setAuthState('signed-out');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const off = onUnauthorized(() => setAuthState('signed-out'));
    return () => {
      off();
    };
  }, []);

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      /* already invalid — fine */
    } finally {
      clearAuth();
      setAuthState('signed-out');
      navigate('/signin', { replace: true });
    }
  };

  if (auth === 'checking') {
    return (
      <div className="app">
        <div className="canvas-empty">
          <span className="spinner" style={{ width: 20, height: 20 }} />
        </div>
      </div>
    );
  }

  if (auth === 'unreachable') {
    return (
      <div className="app">
        <div className="canvas-empty">
          <div>
            <div style={{ fontSize: 28, marginBottom: 10 }}>⚠</div>
            <div style={{ fontWeight: 650, marginBottom: 6 }}>{BRAND.name} is restarting</div>
            <div className="muted" style={{ maxWidth: 420 }}>
              The server didn't answer. Give it a few seconds and retry.
            </div>
            <button className="btn" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (auth === 'signed-out') {
    // The home page is public (it's the product's front door); everything else needs a session.
    if (location.pathname === '/') {
      return (
        <div className="app">
          <header className="tm-header">
            <NavLink to="/" className="tm-brand" aria-label={`${BRAND.name} home`}>
              <BrandMark size={28} />
              <span className="tm-brand-name">{BRAND.name}</span>
            </NavLink>
            <div className="tm-header-grow" />
            <nav className="tm-lp-nav" aria-label="Page sections">
              <a href="#tokens">Tokens</a>
              <a href="#how">How it works</a>
              <a href="#traps">The traps</a>
              <a href="#answer">The verdict</a>
              <a href="#faq">FAQ</a>
            </nav>
            <div className="tm-header-actions">
              <a className="tm-social-btn" href={BRAND.x} target="_blank" rel="noreferrer" aria-label={`${BRAND.name} on X`}>
                <IconX size={14} />
              </a>
              <a className="tm-social-btn" href={BRAND.telegram} target="_blank" rel="noreferrer" aria-label={`${BRAND.name} on Telegram`}>
                <IconTelegram size={14} />
              </a>
              <NavLink to="/signin" className="tm-btn primary sm tm-signin-btn">
                Sign in
              </NavLink>
            </div>
          </header>
          <Landing signedOut />
        </div>
      );
    }
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }

  return (
    <div className="app">
      <header className="tm-header">
        <NavLink to="/" className="tm-brand" aria-label={`${BRAND.name} home`}>
          <BrandMark size={28} />
          <span className="tm-brand-name">{BRAND.name}</span>
        </NavLink>

        {location.pathname !== '/' ? <SearchCommand /> : <div className="tm-header-grow" />}

        <div className="tm-header-actions">
          <a className="tm-social-btn" href={BRAND.x} target="_blank" rel="noopener noreferrer" aria-label={`${BRAND.name} on X`}>
            <IconX size={14} />
          </a>
          <a className="tm-social-btn" href={BRAND.telegram} target="_blank" rel="noopener noreferrer" aria-label={`${BRAND.name} on Telegram`}>
            <IconTelegram size={14} />
          </a>
          <NavLink to="/watchlist" className={({ isActive }) => `tm-iconbtn ${isActive ? 'on' : ''}`} title="Watchlist">
            <IconStar size={18} />
          </NavLink>
          {auth === 'signed-in' && <UserMenu email={email} onLogout={logout} />}
        </div>
      </header>

      {location.pathname.startsWith('/map/') && <TokenTicker current={location.pathname.split('/')[3]} />}

      <Outlet />

      {toast && (
        <div className="tm-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

function UserMenu({ email, onLogout }: { email: string; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);
  return (
    <div className="tm-user" ref={ref}>
      <button className="tm-avatar" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} title={email}>
        {(email || '?').slice(0, 1).toUpperCase()}
      </button>
      {open && (
        <div className="tm-menu" role="menu">
          <div className="tm-menu-email">{email}</div>
          <button role="menuitem" onClick={onLogout}>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
